import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import SEO from '../../../components/common/SEO';
import { toast } from 'react-toastify';
import ContentLoader from '../../../components/common/ContentLoader';

const MODULE_CONFIG = {
    'holidays': { backendKey: 'holiday-package', label: 'Holiday Packages' },
    'hotels': { backendKey: 'hotel', label: 'Hotels' },
    'cabs': { backendKey: 'cab', label: 'Cabs' },
    'doctors': { backendKey: 'doctor', label: 'Doctors' },
    'hospitals': { backendKey: 'hospital', label: 'Hospitals' },
    'yatras': { backendKey: 'yatra', label: 'Yatras' },
};

const ListingApprovals = () => {
    const { module } = useParams();
    const navigate = useNavigate();

    // Fallback if invalid module
    useEffect(() => {
        if (!MODULE_CONFIG[module]) {
            navigate('/superadmin/dashboard');
        }
    }, [module, navigate]);

    const currentConfig = MODULE_CONFIG[module] || MODULE_CONFIG['holidays'];
    const backendModule = currentConfig.backendKey;

    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (MODULE_CONFIG[module]) {
            fetchListings();
        }
    }, [module]);

    const fetchListings = async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get(`/superadmin/listings/${backendModule}`);
            setListings(response.data.data || []);
        } catch (error) {
            console.error('Error fetching listings', error);
            toast.error('Failed to load listings');
        } finally {
            setLoading(false);
        }
    };

    const renderStatusBadge = (status) => {
        let badgeClass = 'bg-secondary';
        if (status === 'Published') badgeClass = 'bg-success';
        else if (status === 'Under Review') badgeClass = 'bg-warning';
        else if (status === 'Rejected') badgeClass = 'bg-danger';

        return <span className={`badge ${badgeClass}`}>{status || 'Published'}</span>;
    };

    return (
        <div className="row">
            <SEO title="Listing Approvals | Super Admin" />
            
            <div className="col-lg-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h4 className="card-title mb-0">{currentConfig.label} Approvals</h4>
                    </div>
                    
                    <div className="card-body">
                        {loading ? (
                            <ContentLoader />
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>ID</th>
                                            <th>Listing Title</th>
                                            <th>Vendor Name</th>
                                            <th>Vendor Contact</th>
                                            <th>Status</th>
                                            <th>Created At</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {listings.length > 0 ? (
                                            listings.map(item => (
                                                <tr key={item.id}>
                                                    <td>{item.id}</td>
                                                    <td>
                                                        <span className="fw-medium">{item.title || 'N/A'}</span>
                                                        {item.status === 'Rejected' && item.rejection_reason && (
                                                            <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '200px' }}>
                                                                <i className="ri-error-warning-line align-middle me-1"></i>
                                                                {item.rejection_reason}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{item.vendor?.name || 'Unknown'}</td>
                                                    <td>
                                                        {item.vendor?.email && <div>{item.vendor.email}</div>}
                                                        {item.vendor?.phone && <div><i className="ri-phone-line me-1"></i>{item.vendor.phone}</div>}
                                                    </td>
                                                    <td>{renderStatusBadge(item.status)}</td>
                                                    <td>{item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A'}</td>
                                                    <td>
                                                        <Link 
                                                            to={`/superadmin/listings-approval/${module}/${item.id}`}
                                                            className="btn btn-sm btn-primary"
                                                        >
                                                            <i className="ri-eye-line me-1"></i> View Details
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">No listings found for this module.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default ListingApprovals;
